const d3 = require('d3');
const vars = require('../vars');
const overlayMargin = {
  top: vars.margins.double,
  bottom: vars.margins.double,
  left: vars.margins.default,
  right: vars.margins.default,
};

export default function (chartEl, prices, boxes, yAxisLimits) {
  let focusDot, comparisonArea;

  var parseDate = d3.timeFormat('%d %b %Y'),
    bisectDate = d3.bisector(function (d) {
      return d.date;
    }).left,
    formatValue = d3.format(',.2f'),
    formatCurrency = function (d) {
      if (d < 0) {
        return '-$' + formatValue(d * -1);
      } else {
        return '$' + formatValue(d);
      }
    };

  var x = d3.scaleTime().range([0, getWidth()]);

  var y = d3.scaleLinear().range([getHeight(), 0]);

  var xAxis = d3.axisBottom().ticks(6).scale(x);

  var yAxis = d3.axisLeft().ticks(4).tickSize(-getWidth()).scale(y);

  var line = d3
    .line()
    .x(function (d) {
      return x(d.date);
    })
    .y(function (d) {
      return y(d.close);
    })
    .curve(d3.curveMonotoneX);

  var container = d3.select(chartEl);

  var svg = container
    .append('svg')
    .attr('class', 'line-chart__svg')
    .attr('width', getWidth())
    .attr('height', getHeight());

  var svgGroup = svg.append('g');

  function getWidth() {
    return chartEl.clientWidth;
  }

  function getHeight() {
    return 240;
  }

  function appendGradient(el, name, config) {
    if (chartEl.getAttribute('data-politician-id') && !chartEl.getAttribute('data-show-alt-activity-chart')) {
      name += '2';
    }

    el.append('linearGradient')
      .attr('id', name)
      .attr('gradientUnits', 'userSpaceOnUse')
      .attr('x1', 0)
      .attr('y1', '0%')
      .attr('x2', 0)
      .attr('y2', '100%')
      .selectAll('stop')
      .data(config)
      .enter()
      .append('stop')
      .attr('offset', function (d) {
        return d.offset;
      })
      .attr('stop-color', function (d) {
        return d.color;
      });
  }

  appendGradient(svgGroup, 'gradientDefault', [
    { offset: '0%', color: '#5A5134' },
    { offset: '75%', color: vars.colors.secondary.hex },
    { offset: '100%', color: vars.colors.secondary.hex },
  ]);

  appendGradient(svgGroup, 'gradientStrong', [
    { offset: '0%', color: '#D0A825' },
    { offset: '100%', color: vars.colors.secondary.hex },
  ]);

  var areaBelowChart = d3
    .area()
    .x(function (d) {
      return x(d.date);
    })
    .y0(getHeight())
    .y1(function (d) {
      return y(d.close);
    })
    .curve(d3.curveMonotoneX);

  var tooltip = container.append('div').attr('class', 'line-chart__tooltip');

  function setOverlaySize(overlay, overlayMargin) {
    overlay
      .attr('width', getWidth() + overlayMargin.left + overlayMargin.right)
      .attr('height', getHeight() + overlayMargin.top + overlayMargin.bottom)
      .attr(
        'transform',
        'translate(' + -overlayMargin.left + ', ' + -overlayMargin.top + ')'
      );
  }

  function process(prices) {
    prices = prices.sort(function (a, b) {
      return a.date - b.date;
    });

    x.domain([prices[0].date, prices[prices.length - 1].date]);
    y.domain([yAxisLimits.min, yAxisLimits.max]);

    svgGroup
      .append('g')
      .attr('class', 'line-chart__x-axis')
      .attr('transform', 'translate(0, ' + getHeight() + ')')
      .call(xAxis);

    svgGroup
      .append('path')
      .datum(prices)
      .attr('class', 'line-chart__line')
      .attr('d', line);

    svgGroup
      .append('path')
      .datum(prices)
      .attr('class', 'line-chart__area-below-chart')
      .attr('d', areaBelowChart);

    comparisonArea = svgGroup
      .append('path')
      .attr('class', 'line-chart__comparison-area');

    svgGroup
      .append('g')
      .attr('class', 'line-chart__y-axis')
      .attr('transform', 'translate(' + vars.margins.double + ', 0)')
      .call(yAxis);

    focusDot = svgGroup
      .append('g')
      .attr('class', 'line-chart__focus-dot')
      .style('display', 'none');

    focusDot.append('circle').attr('r', 4);

    var overlay = svgGroup.append('rect').attr('class', 'line-chart__overlay');

    setOverlaySize(overlay, overlayMargin);

    addEventListeners(overlay);
  }

  function addEventListeners(el) {
    el.on('mouseout', hideTooltip)
      .on('mousemove', mousemove)
      .call(
        d3.drag().on('start', dragStart).on('drag', dragging).on('end', dragEnd)
      );
  }

  function hideTooltip() {
    // if currently not dragging
    if (!startDragDatum) {
      focusDot.style('display', 'none');
      tooltip.style('display', 'none');
    }
  }

  function mousemove() {
    focusDot.style('display', null);
    tooltip.style('display', 'inline-block');
    var datum = getClosestDatumMousePos(this);
    setTooltip(datum, formatCurrency(datum.close), parseDate(datum.date), '');

    const activeBoxes = [];
    for (const box of boxes) {
      let dayBuffer = 3;
      if (chartEl.getAttribute('data-politician-id')) {
        dayBuffer = 1;
      }
      if (
        (datum.date >= box.start_date && datum.date <= box.end_date) ||
        (box.is_insider_trade &&
          datum.date >= addDays(box.start_date, -dayBuffer) &&
          datum.date <= addDays(box.end_date, dayBuffer))
      ) {
        activeBoxes.push(box);
      }
    }
    showTransactionDetailsOf(activeBoxes);
  }

  function showTransactionDetailsOf(boxes) {
    const transactionDetailsEl = chartEl.nextElementSibling;

    if (boxes.length < 1 || !transactionDetailsEl) {
      return;
    }

    const changesEl = transactionDetailsEl.querySelector(
      '.js-transaction-details-changes'
    );
    changesEl.innerHTML = '';

    const activeBoxes = chartEl.querySelectorAll('.line-chart-box--active');
    for (const activeBox of activeBoxes) {
      activeBox.classList.remove('line-chart-box--active');
    }

    for (const box of boxes) {
      const boxEl = chartEl.querySelector('.js-line-chart-box-' + box.uid);
      if (boxEl) {
        boxEl.classList.add('line-chart-box--active');
      }

      const minPrice = formatCurrency(box.min_price_range);
      const maxPrice = formatCurrency(box.max_price_range);
      const avgPrice = formatCurrency(box.price);

      const template = document
        .querySelector('.js-transactiopn-details-template')
        .content.cloneNode(true);
      template.querySelector('.js-transaction-details-guru').innerText =
        box.guru.name;
      template
        .querySelector('.js-transaction-details-guru')
        .setAttribute(
          'href',
          box.guru.guru_transaction_path.replace(
            'symbol',
            document.querySelector('.js-transactiopn-details-template').dataset
              .stockSymbol
          )
        );
      if (!box.guru.index_guru_transactions_page) {
        template.querySelector('.js-transaction-details-guru').setAttribute('rel', 'nofollow');
      }
      template.querySelector('.js-transaction-details-date').innerText =
        box.date_formatted;
      template.querySelector('.js-transaction-details-shares').innerText =
        box.change_in_words;
      template
        .querySelector('.js-transaction-details-indicator')
        .classList.toggle(
          'transaction-details__indicator--sell',
          box.type === 'sell'
        );
      template
        .querySelector('.js-transaction-details-indicator')
        .classList.toggle(
          'transaction-details__indicator--insider-trade',
          box.is_insider_trade
        );

      const priceRangeEl = transactionDetailsEl.querySelector(
        '.js-transaction-details-price-range'
      );
      if (box.min_price_range && box.max_price_range) {
        priceRangeEl.parentNode.classList.remove('utilities__hidden');
        priceRangeEl.innerText = minPrice + ' - ' + maxPrice;
      } else {
        priceRangeEl.parentNode.classList.add('utilities__hidden');
      }
      transactionDetailsEl.querySelector(
        '.js-transaction-details-avg-price'
      ).innerText = avgPrice;
      changesEl.appendChild(template);
    }
  }

  function addDays(date, days) {
    var result = new Date(date);
    result.setDate(result.getUTCDate() + days);
    return result;
  }

  function setTooltip(datum, priceText, dateText, className) {
    tooltip.html(
      '<span class="' + className + '">' + priceText + '</span> ' + dateText
    );

    var translateX;
    var focusDotLeft =
      focusDot.node().getBoundingClientRect().left -
      svg.node().getBoundingClientRect().left;
    var tooltipWidth = tooltip.node().offsetWidth;
    if (
      focusDotLeft + tooltipWidth + vars.margins.default >=
      getWidth() + (window.innerWidth - getWidth()) / 2
    ) {
      translateX = x(datum.date) - vars.margins.half - tooltipWidth;
    } else {
      translateX = x(datum.date) + vars.margins.half;
    }
    tooltip.style(
      'transform',
      'translate(' +
      translateX +
      'px,' +
      (y(datum.close) + vars.margins.default) +
      'px)'
    );
    focusDot.attr(
      'transform',
      'translate(' + x(datum.date) + ',' + y(datum.close) + ')'
    );
  }

  function getClosestDatumMousePos(context) {
    var closestDate = x.invert(d3.mouse(context)[0] - overlayMargin.left);
    var index = bisectDate(prices, closestDate, 1);
    index = index < prices.length ? index : prices.length - 1;
    var prevDatum = prices[index - 1];
    var nextDatum = prices[index];
    var datum =
      closestDate - prevDatum.date > nextDatum.date - closestDate
        ? nextDatum
        : prevDatum; // which date is closer
    return datum;
  }

  var startDragDatum;
  function dragStart() {
    mousemove.apply(this);
    startDragDatum = getClosestDatumMousePos(this);
    comparisonArea.style('display', null);
  }

  function dragging() {
    var datum = getClosestDatumMousePos(this);
    var startDatum;
    var endDatum;
    if (Date.parse(datum.date) < Date.parse(startDragDatum.date)) {
      startDatum = datum;
      endDatum = startDragDatum;
    } else {
      startDatum = startDragDatum;
      endDatum = datum;
    }

    var priceDiff = endDatum.close - startDatum.close;
    var priceDiffRel =
      Math.round(
        ((endDatum.close - startDatum.close) / startDatum.close) * 100 * 100
      ) / 100;
    var className =
      priceDiff < 0
        ? 'line-chart__tooltip-price--negative'
        : 'line-chart__tooltip-price--positive';
    var priceText =
      (priceDiff < 0 ? '' : '+') +
      formatCurrency(priceDiff) +
      ' (' +
      (priceDiffRel < 0 ? priceDiffRel * -1 : priceDiffRel) +
      '%)';
    var dateText =
      parseDate(startDatum.date) + ' - ' + parseDate(endDatum.date);

    setTooltip(datum, priceText, dateText, className);

    var filteredPrices = prices.filter((price) =>
      datumBetween(price.date, startDatum.date, endDatum.date)
    );
    comparisonArea.datum(filteredPrices).attr('d', areaBelowChart);
  }

  function datumBetween(date, startDate, endDate) {
    return (
      Date.parse(date) >= Date.parse(startDate) &&
      Date.parse(date) <= Date.parse(endDate)
    );
  }

  function dragEnd() {
    comparisonArea.style('display', 'none');
    comparisonArea.datum([]).attr('d', areaBelowChart);
    startDragDatum = undefined;
    mousemove.apply(this);
  }

  function resize(newPrices, newYAxisLimits) {
    prices = newPrices;
    yAxisLimits = newYAxisLimits;

    const width = getWidth();
    const height = getHeight();

    x.range([0, width]);
    y.range([height, 0]);

    x.domain([prices[0].date, prices[prices.length - 1].date]);
    y.domain([yAxisLimits.min, yAxisLimits.max]);

    yAxis.tickSize(-width);

    svgGroup.select('.line-chart__x-axis').call(xAxis);
    svgGroup.select('.line-chart__y-axis').call(yAxis);

    svg.attr('width', width).attr('height', height);

    const overlay = svgGroup.select('.line-chart__overlay');
    setOverlaySize(overlay, overlayMargin);

    // add new event listeners
    addEventListeners(overlay);

    svgGroup.select('.line-chart__line').datum(prices).attr('d', line);

    svgGroup
      .select('.line-chart__area-below-chart')
      .datum(prices)
      .attr('d', areaBelowChart);
  }

  process(prices);

  return {
    resize: resize,
    showTransactionDetailsOf: showTransactionDetailsOf,
  };
}
